import { FocusTrap } from "@iunctim/component-overlay/Resources/Public/Scripts/features/FocusTrap.js";
import { ScrollLock } from "@iunctim/component-overlay/Resources/Public/Scripts/features/ScrollLock.js";

/**
 * Class to implement the modal functionality
 * for a single instance; the application is done
 * via the ModalManager class
 */
export class Modal {
    private openers: HTMLElement[];
    private closers: NodeListOf<HTMLElement>;
    private focusTrap: FocusTrap;

    /**
     * Constructor of the Modal
     *
     * @param {HTMLElement} modal
     */
    public constructor(private modal: HTMLElement) {
        this.openers = this.selectOpeners();
        this.closers = this.selectClosers();
        this.focusTrap = new FocusTrap(this.modal);

        this.useButtons();
        this.useEscapeKey();
    }

    /**
     * Method to open the modal
     *
     * @param {HTMLElement | null} opener
     * @returns {void}
     */
    public open(opener?: HTMLElement): void {
        if (this.active) return;

        opener ??=
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : undefined;
        // Show the modal
        this.modal.hidden = false;
        ScrollLock.use().lock();

        // Get the focus-trap and enable it
        this.focusTrap.enable(opener);

        // Set aria-expanded to true on all
        // openers for this specific modal
        this.openers.forEach((opener) => {
            opener.setAttribute("aria-expanded", "true");
        });
    }

    /**
     * Method to close the modal
     *
     * @returns {void}
     */
    public close(): void {
        if (!this.active) return;

        // Hide the modal
        this.modal.hidden = true;
        ScrollLock.use().unlock();

        // Get the focus-trap and disable it
        this.focusTrap.disable();

        // Set aria-expanded to false on all
        // openers for this specific modal
        this.openers.forEach((opener) => {
            opener.setAttribute("aria-expanded", "false");
        });
    }

    /**
     * Method to handle the opening and closing via buttons
     *
     * @returns {void}
     */
    private useButtons(): void {
        // Handle the openers
        this.openers.forEach((opener) => {
            opener.addEventListener("click", () => this.open(opener));
        });

        // Handle the closers
        this.closers.forEach((closer) => {
            closer.addEventListener("click", () => this.close());
        });
    }

    /**
     * Method to handle the closing via escape-key
     *
     * @returns {void}
     */
    private useEscapeKey(): void {
        this.modal.addEventListener("keydown", ({ key }) => {
            if (
                this.active &&
                key === "Escape" &&
                this.modal.dataset.closeOnEscape !== "false"
            )
                this.close();
        });
    }

    /**
     * Selects the modal openers
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    private selectOpeners(): HTMLElement[] {
        return Array.from(
            document.querySelectorAll<HTMLElement>("[aria-controls]"),
        ).filter(
            (element) =>
                element.getAttribute("aria-controls") === this.modal.id,
        );
    }

    /**
     * Selects the modal closers
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    private selectClosers(): NodeListOf<HTMLElement> {
        return this.modal.querySelectorAll(`[close]`);
    }

    /**
     * Helper method to determine whether
     * the modal is open or closed
     *
     * @returns {boolean}
     */
    private get active(): boolean {
        return !this.modal.hidden;
    }
}
